import { useState } from 'react';
import type { CalculatorFormData, StepConfig, ValidationErrors } from '../calculatorTypes';
import { NumberInput } from '../shared/NumberInput';
import { getText } from '../calculatorUtils';
import type { CalcTexts } from '../locales';
import styles from '../Calculator.module.css';

interface Props {
  step: StepConfig;
  formData: CalculatorFormData;
  onNext: (data: Partial<CalculatorFormData>) => void;
  onBack: (data: Partial<CalculatorFormData>) => void;
  canGoBack: boolean;
  texts: CalcTexts;
}

export function HousingStep({ step, formData, onNext, onBack, canGoBack, texts }: Props) {
  const fields = step.fields!;
  const checkboxKey = step.checkboxKey as keyof CalculatorFormData;

  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      fields.map(f => [f.key, (formData[f.key as keyof CalculatorFormData] as string) || ''])
    )
  );
  const [isChecked, setIsChecked] = useState<boolean>(
    (formData[checkboxKey] as boolean) ?? false
  );
  const [errors, setErrors] = useState<ValidationErrors>({});

  function validate(): boolean {
    if (isChecked) return true;
    const newErrors: ValidationErrors = {};
    for (const field of fields) {
      const v = values[field.key];
      if (v === '' || v === undefined) {
        newErrors[field.key] = texts.validation.required;
      } else if (field.max !== undefined && parseInt(v, 10) > field.max) {
        newErrors[field.key] = texts.validation.housing_costs_out_of_range;
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function buildData(coerceEmpty = false): Partial<CalculatorFormData> {
    const data: Partial<CalculatorFormData> = { [checkboxKey]: isChecked } as Partial<CalculatorFormData>;
    for (const field of fields) {
      (data as Record<string, unknown>)[field.key] = isChecked ? '0' : (coerceEmpty ? values[field.key] || '0' : values[field.key]);
    }
    return data;
  }

  function handleNext() {
    if (!validate()) return;
    onNext(buildData(true));
  }

  function handleCheckbox(e: React.ChangeEvent<HTMLInputElement>) {
    setIsChecked(e.target.checked);
    if (e.target.checked) setErrors({});
  }

  const hintText = step.hintKey ? getText(texts, step.hintKey) : undefined;

  return (
    <div className={styles.stepContent}>
      <p className={styles.stepQuestion}>{getText(texts, step.questionKey!)}</p>

      <div className={styles.incomeFieldsGroup}>
        {fields.map(field => (
          <div key={field.key} className={styles.incomeFieldRow}>
            <div className={styles.incomeRow}>
              <NumberInput
                id={field.key}
                value={values[field.key]}
                onChange={v => {
                  setValues(prev => ({ ...prev, [field.key]: v }));
                  if (errors[field.key]) setErrors(prev => ({ ...prev, [field.key]: '' }));
                }}
                label={getText(texts, field.labelKey)}
                error={!!errors[field.key]}
                disabled={isChecked}
                max={field.max}
              />
              <label htmlFor={field.key} className={styles.incomeLabel}>
                {getText(texts, field.labelKey)}
              </label>
            </div>
            {errors[field.key] && (
              <p className={styles.validationError}>{errors[field.key]}</p>
            )}
          </div>
        ))}
      </div>

      <label className={styles.checkboxRow}>
        <input
          type="checkbox"
          checked={isChecked}
          onChange={handleCheckbox}
          className={styles.checkbox}
        />
        <span>{getText(texts, step.checkboxLabelKey!)}</span>
      </label>

      {hintText && (
        <div className={styles.hintBox}>
          <span className={styles.hintLabel}>{getText(texts, 'common.labels.note')}: </span>
          {hintText}
        </div>
      )}

      <div className={styles.stepNav}>
        <button
          type="button"
          className={styles.btnBack}
          onClick={() => onBack(buildData())}
          disabled={!canGoBack}
        >
          {'< '}{texts.common.buttons.back}
        </button>
        <button type="button" className={styles.btnSecondary} onClick={handleNext}>
          {texts.common.buttons.continue}
        </button>
      </div>
    </div>
  );
}
